import {
  documentNatureLabel,
  MARCHE_INITIAL_FUNCTIONS,
} from "@etudescarre/constants";
import type { DataTableColumn } from "@etudescarre/ui";
import { Button } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  CubeIcon,
  FileArrowDownIcon,
  FileTextIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { useYardContext } from "@/providers/yard-context";
import { Classeur, type FilterDef } from "../components/classeur";
import { useClasseurFilters } from "../components/classeur/use-classeur-data";
import { openFichier } from "../components/document-table/open-pieces";
import {
  DossierMarcheDocumentForm,
  DossierMarcheMaquetteForm,
} from "../components/dossier-marche/dossier-marche-panels";

// Écran 001 « Dossier marché ». Deux capacités réconciliées sur le même écran :
//  - visualiser les pièces marché existantes (lecture seule) ;
//  - enregistrer des pièces du marché initial via deux flux indépendants
//    « ajouter un document seul » / « ajouter une maquette seule ».
type MarketPieceItem = {
  id: string;
  number: string;
  title: string;
  author: string | null;
  nature: string | null;
  indice: string | null;
  zoneName: string | null;
  fichierId: number | null;
  fichierNativeId: number | null;
  bimAvailable: boolean;
};

type FetchState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; items: MarketPieceItem[] };

type Flow = "document" | "maquette" | null;

const natureLabel = (nature: string | null) =>
  isNotNil(nature) ? documentNatureLabel(nature) : "-";

/** Build the filter option list for a column, deduplicated and sorted. */
function optionsFrom(
  items: MarketPieceItem[],
  pick: (item: MarketPieceItem) => string | null,
  label: (value: string) => string = (v) => v,
) {
  const values = [...new Set(items.map(pick).filter(isNotNil))].sort((a, b) =>
    a.localeCompare(b, "fr"),
  );
  return values.map((value) => ({ label: label(value), value }));
}

const REGISTER_REFUSAL =
  "L'enregistrement du marché initial est réservé au directeur et au conducteur de travaux.";

export function DossierMarche() {
  const { yardId } = useParams<{ yardId: string }>();
  const { contracts } = useYardContext();
  const canRegister = contracts.some(
    (c) =>
      c.type === "travaux_n_1" &&
      c.isActive &&
      isNotNil(c.function) &&
      MARCHE_INITIAL_FUNCTIONS.includes(c.function),
  );
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [flow, setFlow] = useState<Flow>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (isNil(yardId)) return;
    // refreshKey is used to trigger a re-fetch after registering a piece
    void refreshKey;
    let cancelled = false;

    async function fetchData() {
      setState({ status: "loading" });
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/market-pieces",
        { params: { path: { yardId: yardId ?? "" } } },
      );
      if (cancelled) return;
      if (error || isNil(data)) {
        setState({ status: "error" });
        return;
      }
      setState({ status: "success", items: data.data.items });
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [yardId, refreshKey]);

  // Returning from a registration flow: reload the listing so freshly added
  // pieces surface, and drop back to the read view.
  const closeFlow = useCallback(() => {
    setFlow(null);
    setRefreshKey((n) => n + 1);
  }, []);

  const items = state.status === "success" ? state.items : [];

  const filters = useMemo<FilterDef[]>(
    () => [
      {
        key: "number",
        label: "N°",
        options: optionsFrom(items, (i) => i.number),
      },
      {
        key: "author",
        label: "Auteur",
        options: optionsFrom(items, (i) => i.author),
      },
      {
        key: "nature",
        label: "Nature",
        options: optionsFrom(items, (i) => i.nature, natureLabel),
      },
      {
        key: "indice",
        label: "Indice",
        options: optionsFrom(items, (i) => i.indice),
      },
      {
        key: "title",
        label: "Intitulé",
        options: optionsFrom(items, (i) => i.title),
      },
      {
        key: "zone",
        label: "Zone",
        options: optionsFrom(items, (i) => i.zoneName),
      },
    ],
    [items],
  );

  const { activeFilters, onFilterChange } = useClasseurFilters({ filters });

  const filteredItems = useMemo(() => {
    const matches = (values: string[], value: string | null) =>
      values.length === 0 || (isNotNil(value) && values.includes(value));
    return items.filter(
      (item) =>
        matches(activeFilters.number ?? [], item.number) &&
        matches(activeFilters.author ?? [], item.author) &&
        matches(activeFilters.nature ?? [], item.nature) &&
        matches(activeFilters.indice ?? [], item.indice) &&
        matches(activeFilters.title ?? [], item.title) &&
        matches(activeFilters.zone ?? [], item.zoneName),
    );
  }, [items, activeFilters]);

  const columns = useMemo<DataTableColumn<MarketPieceItem>[]>(
    () => [
      { id: "number", header: "N°", accessor: "number" },
      { id: "title", header: "Intitulé", accessor: "title" },
      {
        id: "author",
        header: "Auteur",
        accessor: "author",
        cell: ({ value }) => (value as string | null) ?? "-",
      },
      {
        id: "nature",
        header: "Nature",
        accessor: "nature",
        cell: ({ value }) => natureLabel(value as string | null),
      },
      {
        id: "indice",
        header: "Indice",
        accessor: "indice",
        cell: ({ value }) => (value as string | null) ?? "-",
      },
      {
        id: "zoneName",
        header: "Zone",
        accessor: "zoneName",
        cell: ({ value }) => (value as string | null) ?? "-",
      },
      {
        id: "fichier",
        header: "Fichiers",
        accessor: "fichierId",
        cell: ({ row }) => {
          const { fichierId, fichierNativeId } = row;
          if (isNil(fichierId) && isNil(fichierNativeId)) {
            return <span className="text-zinc-300">-</span>;
          }
          return (
            <span className="inline-flex items-center gap-2 text-blue-600">
              {isNotNil(fichierId) && (
                <button
                  type="button"
                  onClick={() => openFichier(yardId ?? "", fichierId)}
                  title="Ouvrir le fichier"
                >
                  <FileTextIcon size={18} />
                </button>
              )}
              {isNotNil(fichierNativeId) && (
                <button
                  type="button"
                  onClick={() => openFichier(yardId ?? "", fichierNativeId)}
                  title="Télécharger le fichier natif"
                >
                  <FileArrowDownIcon size={18} />
                </button>
              )}
            </span>
          );
        },
      },
      {
        id: "bim",
        header: "BIM",
        accessor: "bimAvailable",
        cell: ({ value }) =>
          value ? (
            <CubeIcon size={18} className="text-emerald-600" />
          ) : (
            <span className="text-zinc-300">-</span>
          ),
      },
    ],
    [yardId],
  );

  if (isNotNil(flow)) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
          <button
            type="button"
            onClick={() => setFlow(null)}
            className="mb-2 text-sm text-blue-600 hover:underline dark:text-blue-400"
          >
            ← Retour
          </button>
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {flow === "document"
              ? "Ajouter un document"
              : "Ajouter une maquette"}
          </h2>
        </div>
        <div className="flex-1 overflow-auto p-6">
          <div className="mx-auto max-w-3xl">
            {flow === "document" ? (
              <DossierMarcheDocumentForm onClose={closeFlow} />
            ) : (
              <DossierMarcheMaquetteForm onClose={closeFlow} />
            )}
          </div>
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 py-16">
        <p className="text-red-600">Impossible de charger les pièces marché.</p>
      </div>
    );
  }

  return (
    <Classeur
      title="Pièces marché travaux"
      items={filteredItems}
      columns={columns}
      filters={filters}
      activeFilters={activeFilters}
      onFilterChange={onFilterChange}
      isLoading={state.status === "loading"}
      emptyMessage="Aucune pièce marché"
      total={filteredItems.length}
      actions={
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
            <Button
              type="button"
              disabled={!canRegister}
              onClick={() => setFlow("document")}
            >
              Ajouter un document
            </Button>
            <Button
              type="button"
              outline
              disabled={!canRegister}
              onClick={() => setFlow("maquette")}
            >
              Ajouter une maquette
            </Button>
          </div>
          {!canRegister && (
            <p className="text-xs text-zinc-500">{REGISTER_REFUSAL}</p>
          )}
        </div>
      }
    />
  );
}
